/**
 * CAPA module – shared constants and URL helpers for global Create screen.
 * @see ./00-capa-module-spec.md
 */

export const CAPA_ENTITY_TYPES = {
  STUDY: "study",
  STAFF: "staff",
  DOCUMENT: "document",
  FOLDER: "folder",
};

export const CAPA_RISK_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
  { value: "Critical", label: "Critical" },
];

/** Standard root cause analysis techniques (values stored on CAPA `RcaMethod`). */
/** Implementation task status (assignee updates; auth modal requires description). */
export const CAPA_IMPLEMENTATION_STATUS_OPTIONS = [
  { value: "Not Started", label: "Not Started" },
  { value: "In Progress", label: "In Progress" },
  { value: "Partially Completed", label: "Partially Completed" },
  { value: "Completed", label: "Completed" },
  { value: "Deferred", label: "Deferred" },
];

/** Tasks in these statuses cannot proceed until every row is outside this set. */
export const CAPA_IMPLEMENTATION_BLOCKING_FOR_VERIFICATION = new Set([
  "Not Started",
  "In Progress",
]);

/** Verifier outcome when completing a verification request (auth modal required). */
export const CAPA_VERIFICATION_OUTCOME_OPTIONS = [
  { value: "Effective", label: "Effective" },
  { value: "Not Effective", label: "Not Effective" },
];

export const CAPA_RCA_METHOD_OPTIONS = [
  { value: "5_whys", label: "5 Whys" },
  { value: "fishbone", label: "Fishbone (Ishikawa)" },
  { value: "pareto", label: "Pareto analysis" },
  { value: "fta", label: "Fault tree analysis (FTA)" },
  { value: "fmea", label: "FMEA" },
  { value: "dmaic", label: "DMAIC" },
  { value: "affinity", label: "Affinity diagram" },
  { value: "scatter", label: "Scatter diagram" },
  { value: "brainstorming", label: "Brainstorming" },
  { value: "other", label: "Other" },
];

/** List badge backgrounds — match risk pill colors (CapaCreate / CapaPages). */
export const CAPA_RISK_BADGE_BG = {
  low: "#22c55e",
  medium: "#eab308",
  high: "#f97316",
  critical: "#ef4444",
};

/**
 * @param {string | undefined} risk
 * @returns {{ backgroundColor: string; color: string }}
 */
export function getCapaRiskBadgeStyle(risk) {
  const key = String(risk || "Medium").trim().toLowerCase();
  const bg = CAPA_RISK_BADGE_BG[key] || "#6c757d";
  const color = key === "medium" ? "#1f2937" : "#fff";
  return { backgroundColor: bg, color };
}

/** CAPA workflow status — list badges (distinct from risk colors). */
export const CAPA_STATUS_BADGE_BG = {
  open: "#3b82f6",
  "under investigation": "#6366f1",
  "under rca": "#8b5cf6",
  "action planning": "#14b8a6",
  approved: "#22c55e",
  implementation: "#0ea5e9",
  /** @deprecated Legacy CAPA lifecycle status string */
  "in progress": "#0ea5e9",
  verification: "#fbbf24",
  closure: "#f59e0b",
  closed: "#64748b",
};

/**
 * @param {string | undefined} status
 * @returns {{ backgroundColor: string; color: string }}
 */
export function getCapaStatusBadgeStyle(status) {
  const key = String(status || "Open").trim().toLowerCase();
  const bg = CAPA_STATUS_BADGE_BG[key] || "#6c757d";
  const darkText = key === "verification" || key === "closure";
  return { backgroundColor: bg, color: darkText ? "#1f2937" : "#fff" };
}

/** Values must match main ISF (`isf.jsx`) — API expects `eISF` / `ePSF`, not template display names. */
export const CAPA_ISF_TEMPLATE_CATEGORIES = [
  { value: "eISF", label: "eISF" },
  { value: "ePSF", label: "ePSF" },
];

/**
 * Normalizes category strings from older links or admin UIs to API values.
 * @param {string} [raw]
 * @returns {string}
 */
export function normalizeIsfTemplateCategoryForApi(raw) {
  if (raw == null || String(raw).trim() === "") return "";
  const v = String(raw).trim();
  if (v === "eISF Default Template") return "eISF";
  if (v === "ePSF Default Template") return "ePSF";
  return v;
}

export function buildCapaContextFromIsfDocument({
  docId,
  docLabel,
  studyId,
  studyLabel,
  templateCategory,
}) {
  const entityId = String(docId || "").trim();
  return {
    locked: Boolean(entityId),
    module: "isf",
    entityType: "document",
    entityId,
    entityLabel: String(docLabel || "").trim() || entityId,
    studyId: String(studyId || "").trim() || undefined,
    studyLabel: String(studyLabel || "").trim() || undefined,
    templateCategory: normalizeIsfTemplateCategoryForApi(templateCategory) || undefined,
  };
}

export function buildCapaContextFromStudy({ studyId, studyLabel }) {
  const entityId = String(studyId || "").trim();
  return {
    locked: Boolean(entityId),
    module: "study_management",
    entityType: "study",
    entityId,
    entityLabel: String(studyLabel || "").trim() || entityId,
  };
}

export function buildCapaContextFromStaff({ staffId, staffLabel }) {
  const entityId = String(staffId || "").trim();
  return {
    locked: Boolean(entityId),
    module: "user_management",
    entityType: "staff",
    entityId,
    entityLabel: String(staffLabel || "").trim() || entityId,
  };
}

/** Display label for a user row (User Management list / CAPA context). */
export function staffRowCapaDisplayLabel(row) {
  if (!row) return "";
  const name = String(row.Name || row.name || "").trim();
  const email = String(row.EmailId || row.emailId || "").trim();
  const emp = String(row.EmployeeNum || row.employeeNum || "").trim();
  if (name && emp) return `${name} — ${emp}`;
  if (name) return name;
  if (email) return email;
  return staffRowCapaId(row);
}

export function staffRowCapaId(row) {
  if (!row) return "";
  return String(row.StaffId ?? row.staffId ?? row.StaffIDRef ?? "").trim();
}

/** Display label for a study row (Study Management list / CAPA context). */
export function studyRowCapaDisplayLabel(row) {
  if (!row) return "";
  const num = row.StudyNumber || row.StudyCode || "";
  const name = row.StudyName || "";
  if (num && name) return `${num} — ${name}`;
  return name || num || String(row.StudyId ?? row.StudyIdRef ?? "").trim();
}

export function studyRowCapaId(row) {
  if (!row) return "";
  return String(row.StudyId ?? row.StudyIdRef ?? row.studyId ?? "").trim();
}

/**
 * @param {Record<string, string | number | undefined | null>} params
 * @returns {string} path + query
 */
export function buildCapaNewUrl(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== "") {
      search.set(k, String(v));
    }
  });
  const q = search.toString();
  return q ? `/capa/new?${q}` : "/capa/new";
}

/**
 * @param {URLSearchParams} searchParams
 */
export function parseCapaContextFromSearchParams(searchParams) {
  const module = searchParams.get("module") || "";
  const entityType = searchParams.get("entityType") || "";
  const entityId = searchParams.get("entityId") || "";
  const entityLabel = searchParams.get("entityLabel") || "";
  const studyId = searchParams.get("studyId") || "";
  const studyLabel = searchParams.get("studyLabel") || "";
  const templateCategory = searchParams.get("templateCategory") || "";
  const locked = Boolean(module && entityType && entityId);
  return {
    locked,
    module: module || undefined,
    entityType: entityType || undefined,
    entityId: entityId || undefined,
    entityLabel: entityLabel ? decodeURIComponent(entityLabel) : undefined,
    studyId: studyId || undefined,
    studyLabel: studyLabel ? decodeURIComponent(studyLabel) : undefined,
    templateCategory: templateCategory || undefined,
  };
}
